import { useState, useRef } from "react";
import PropTypes from "prop-types";
import { TextWrapper, Wrapper, TextWrapperSpan } from "../../../constants/common/StyledGlobalConstants";
import { CustomisedPrimeInput, ErrorContainer } from "../../myContract/ContactMe.styles";
import { StarSpan } from "./CamerasAndOtherAccessories";
import { handleVehicleValidations } from "../../../util/vehicleValidationUtil";
import styled from "styled-components";
import { getPendoId } from "../../../util/cfstPendoUtils";

const VINCompatibilityCheckContainer = styled(Wrapper)`
  margin-top: 1rem;
  width: 22.188rem;
  @media (max-width: 679px) {
    width: 100%;
  }
`;

function SelectedVINOrPA({ selectedVinOrPA, setSelectedVinOrPA, getDeviceType, isBlockingErrorOccured }) {
  const [vin, setVin] = useState({
    isValid: true,
    errorText: "",
    value: selectedVinOrPA.newVin || "",
  });

  const hasBlurredRef = useRef(false);

  //to fetch the new Vin for current vehicle
  const getVin = (event, eventType) => {
    const validationResult = handleVehicleValidations("vin", event.target.value);
    if (validationResult) {
      const { isValid, errorText, value } = validationResult;
      setVin((prevState) => ({
        ...prevState,
        isValid,
        errorText,
        value,
      }));
      const selectedValue = selectedVinOrPA;
      setSelectedVinOrPA((prevState) => ({ ...prevState, newVin: errorText ? "" : value }));
      if (eventType === "onBlur" && !hasBlurredRef.current && isValid && value && selectedValue.boxModelFetchedVin !== value) {
        hasBlurredRef.current = true;
        getDeviceType({ ...selectedValue, newVin: value });
      }
    }
  };
  // Reset the flag when the component is re-rendered or when necessary
  function resetBlurFlag() {
    hasBlurredRef.current = false;
  }

  return (
    <Wrapper aria-label="Selected vehicle details" background="#F6F6F6" width="100%" marginTop="0.5rem" fontSize="0.875rem" fontWeight="400">
      <Wrapper padding="1rem">
        {selectedVinOrPA.isVin ? (
          <>
            <TextWrapper>
              <TextWrapperSpan color="#6F7171">Vehicle name: </TextWrapperSpan>
              {selectedVinOrPA.vehicleName}
            </TextWrapper>
            <TextWrapper>
              <TextWrapperSpan color="#6F7171">ESN: </TextWrapperSpan>
              {selectedVinOrPA.esn}
            </TextWrapper>
            {selectedVinOrPA.vin && (
              <TextWrapper>
                <TextWrapperSpan color="#6F7171">VIN: </TextWrapperSpan>
                {selectedVinOrPA.vin}
              </TextWrapper>
            )}
            <TextWrapper>
              <TextWrapperSpan color="#6F7171">Registration number: </TextWrapperSpan>
              {selectedVinOrPA.registrationNumber}
            </TextWrapper>
            {!selectedVinOrPA.vin && (
              <Wrapper marginTop="1.5rem">
                <TextWrapperSpan>Enter the following missing information to identify your current vehicle:</TextWrapperSpan>
                <VINCompatibilityCheckContainer>
                  <TextWrapper fontWeight="700" marginBottom="40px !important" lineHeight="0.75rem" fontSize="0.75rem">
                    Vehicle identification number (VIN)<StarSpan>*</StarSpan>
                  </TextWrapper>
                  <CustomisedPrimeInput
                    id="cfst-current-vin"
                    required={!vin.isValid}
                    validGreen={!vin.isValid}
                    value={vin.value}
                    onChange={(event) => getVin(event, "change")}
                    placeholder="Enter VIN to find vehicle details"
                    maxlength="17"
                    style={{ marginTop: "0.5rem" }}
                    onBlur={(event) => getVin(event, "onBlur")}
                    onFocus={resetBlurFlag}
                    disabled={isBlockingErrorOccured}
                  />
                  {!vin.isValid && (
                    <ErrorContainer id="cfst-vin-error" role="alert" tabIndex="-1" aria-live="assertive">
                      {vin.errorText}
                    </ErrorContainer>
                  )}
                  {vin.isValid && vin.value > 0 && (
                    <span className="sr-only" tabIndex="-1" role="alert">
                      Please stop here. System will not accept more than 17 characters. You already entered 17 characters.
                    </span>
                  )}
                  <TextWrapper fontSize="0.75rem" fontWeight="400" lineHeight="1.125rem" marginTop="4px !important" color="#6F7171">
                    Need help finding your VIN?{" "}
                    <TextWrapperSpan
                      isLink
                      aria-label="VIN Learn More"
                      tabIndex={0}
                      textDecorationLine="underline"
                      id={getPendoId("current-vin-learn-more")}
                      onClick={() =>
                        window.open(
                          "https://install.verizonconnect.com/hc/en-us/articles/360060393273-FAQ-Where-can-I-find-the-vehicle-identification-number-VIN"
                        )
                      }
                      onKeyDown={(e) =>
                        (e.key === "Enter" || e.key === " ") &&
                        window.open(
                          "https://install.verizonconnect.com/hc/en-us/articles/360060393273-FAQ-Where-can-I-find-the-vehicle-identification-number-VIN"
                        )
                      }
                      style={{ cursor: "pointer", color: "#006FC1" }}
                    >
                      Learn more
                    </TextWrapperSpan>
                    .
                  </TextWrapper>
                </VINCompatibilityCheckContainer>
              </Wrapper>
            )}
          </>
        ) : (
          <>
            <TextWrapper>
              <TextWrapperSpan color="#6F7171">{selectedVinOrPA.isPoweredAsset ? "Powered asset name: " : "Asset name: "} </TextWrapperSpan>
              {selectedVinOrPA.assetName}
            </TextWrapper>
            <TextWrapper>
              <TextWrapperSpan color="#6F7171">Number: </TextWrapperSpan>
              {selectedVinOrPA.assetNumber}
            </TextWrapper>
            <TextWrapper>
              <TextWrapperSpan color="#6F7171">ESN: </TextWrapperSpan>
              {selectedVinOrPA.esn}
            </TextWrapper>
            {selectedVinOrPA.vin && (
              <TextWrapper>
                <TextWrapperSpan color="#6F7171">VIN: </TextWrapperSpan>
                {selectedVinOrPA.vin}
              </TextWrapper>
            )}
            {selectedVinOrPA.isPoweredAsset && (
              <TextWrapper>
                <TextWrapperSpan color="#6F7171">Registration number: </TextWrapperSpan>
                {selectedVinOrPA.registrationNumber}
              </TextWrapper>
            )}
          </>
        )}
      </Wrapper>
    </Wrapper>
  );
}

SelectedVINOrPA.propTypes = {
  selectedVinOrPA: PropTypes.object.isRequired,
  setSelectedVinOrPA: PropTypes.func.isRequired,
  getDeviceType: PropTypes.func.isRequired,
  isBlockingErrorOccured: PropTypes.bool,
};

export default SelectedVINOrPA;
